// ============================================================
// How the scene's faces, floor and grid are drawn - pure
// ============================================================

import type { SceneFace } from '../../engine/battlefield/scene';
import { boundaryOutline } from '../../engine/battlefield/boundary';
import type { Boundary } from '../../engine/battlefield/boundary';
import type { VoxelGrid } from '../../engine/battlefield/voxels';
import { parseCell } from '../../engine/battlefield/voxels';
import { clipToEye, project, projectSegment, SCENE_VIEW } from '../../engine/battlefield/projection';
import type { Camera } from '../../engine/battlefield/projection';
import { gridSegments } from '../../engine/battlefield/grid';

/**
 * Every face is outlined in its OWN fill.
 *
 * Greedy meshing splits an irregular blob into several boxes, and each
 * box was outlining itself - so a hand-painted hillside read as "a random
 * assortment of mixed volumes" rather than as one surface. Stroking a
 * face in its own colour makes the seams WITHIN a material disappear
 * while the silhouette against the ground stays crisp, and a change of
 * material still reads as an edge because the two colours differ.
 *
 * The stroke is kept rather than removed because a hairline of the same
 * colour also closes the sub-pixel gaps between adjacent polygons, which
 * would otherwise show the ground through a solid wall.
 */

/**
 * What a face says to the hit tests, on either renderer: the hover readout,
 * Build's selection and deploying onto a rooftop read the face under the
 * pointer by these, and E2E parses `data-anchor`.
 */
export const faceHitAttrs = (face: SceneFace) => ({
  'data-testid': `face-${face.solidId}`,
  'data-solid-id': face.solidId,
  'data-kind': face.kind,
  'data-anchor': `${face.anchor.x.toFixed(1)},${face.anchor.y.toFixed(1)}`,
});

export const toPoints = (face: SceneFace) => face.points.map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`).join(' ');

/**
 * The mouth of every hole: the topmost dug cell in each column.
 *
 * One quad per column, not per dug cell - a three-deep trench is still
 * one hole in the ground, and drawing three coincident holes would just
 * cancel itself out under the even-odd rule.
 */
function topmostDugCells(voxels: VoxelGrid | null): { x: number; z: number }[] {
  if (!voxels?.dug) return [];
  const top = new Map<string, number>();
  for (const key of Object.keys(voxels.dug)) {
    const c = parseCell(key);
    const column = `${c.x},${c.z}`;
    const seen = top.get(column);
    if (seen === undefined || c.y > seen) top.set(column, c.y);
  }
  // Only columns broken open at the surface are holes. A sealed cavern
  // deeper down leaves the ground above it intact.
  return [...top.entries()]
    .filter(([, y]) => y === -1)
    .map(([column]) => {
      const [x, z] = column.split(',').map(Number);
      return { x, z };
    });
}

export interface GridLine {
  key: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** The grid (engine/display/floor `gridSegments`) projected, each line cut at the eye (see `projectSegment`). */
export function gridLines(boundary: Boundary, camera: Camera): GridLine[] {
  return gridSegments(boundary).flatMap(({ key, a, b }) => {
    const s = projectSegment(a, b, camera, SCENE_VIEW);
    return s ? [{ key, x1: s.a.x, y1: s.a.y, x2: s.b.x, y2: s.b.y }] : [];
  });
}

/** A ground polygon cut at the eye and projected, as screen points (none when it is all behind the eye). */
function groundOnScreen(corners: { x: number; z: number }[], camera: Camera) {
  const world = clipToEye(
    corners.map((c) => ({ x: c.x, y: 0, z: c.z })),
    camera,
    SCENE_VIEW,
  );
  return world.length < 3 ? [] : world.map((p) => project(p, camera, SCENE_VIEW));
}

/** A projected ground polygon as SVG path commands; empty when none of it is in front of the eye. */
function groundRing(corners: { x: number; z: number }[], camera: Camera): string {
  return groundOnScreen(corners, camera)
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');
}

/** The four ground corners of a voxel column, `size` metres a side. */
function cellCorners(cell: { x: number; z: number }, size: number) {
  return [
    { x: cell.x * size, z: cell.z * size },
    { x: (cell.x + 1) * size, z: cell.z * size },
    { x: (cell.x + 1) * size, z: (cell.z + 1) * size },
    { x: cell.x * size, z: (cell.z + 1) * size },
  ];
}

/**
 * The floor, as a path with a hole for every dug cell.
 *
 * A filled polygon would paint straight over any trench beneath it. An
 * SVG path can have subpaths, and `fill-rule="evenodd"` turns each one
 * into a hole - so the ground is drawn once, with the excavation cut out
 * of it, and the trench walls below simply show through.
 */
export function groundPathFor(boundary: Boundary, camera: Camera, voxels: VoxelGrid | null): string {
  const ring = groundRing(boundaryOutline(boundary), camera);
  if (!ring) return '';
  const holes = topmostDugCells(voxels)
    .map((cell) => groundRing(cellCorners(cell, voxels!.size > 0 ? voxels!.size : 1), camera))
    .filter(Boolean)
    .join(' Z ');
  return `${ring} Z${holes ? ` ${holes} Z` : ''}`;
}

/** The arena's edge on screen, as polygon points. */
export function outlinePoints(boundary: Boundary, camera: Camera): string {
  return groundOnScreen(boundaryOutline(boundary), camera)
    .map((p) => `${p.x.toFixed(2)},${p.y.toFixed(2)}`)
    .join(' ');
}
